import { useAtomValueRawSync } from 'jotai';
import type { ReactElement } from 'react';
import { useRef, useState } from 'react';

import { Hint } from '@/components/occurra/Hint';
import { OccurraMark } from '@/components/occurra/OccurraMark';
import { cn } from '@/lib/utils';
import { isNavLabelShownAtom } from '@/state/ui/shellRecordAtoms';

import { CHAT_SLIDE_MS } from './slideMotion';

/**
 * The mark's rotation is RELATIVE, not a set of poses: every interaction adds
 * a turn to wherever it currently sits. Pointer in turns it 60° one way,
 * pointer out turns it 60° back, and a click turns it a further 120° — the
 * click's sign following the drawer, so the mark unwinds as the chat opens and
 * winds as it closes. Each click therefore reads as "the same gesture that
 * moved the drawer", not as a spin that happens to coincide with it.
 *
 * So the resting angle drifts — enter, click, leave leaves it at ±120° rather
 * than 0. That is deliberate and invisible: the mark is three brackets at 120°
 * apart (see OccurraMark), so 0°, 120° and 240° are the same picture. What the
 * eye gets instead is that no interaction is ever a no-op — a second click
 * turns the mark again rather than snapping it back to an angle it already
 * held.
 *
 * A turn's duration travels with it, because the two kinds are paced
 * differently: a hover is a flick, a click is the drawer's own slide and runs
 * for exactly as long (CHAT_SLIDE_MS) so the two read as one movement.
 *
 * `isHovered` still tracks the pointer on its own, because it drives things that
 * are poses rather than turns: the ring, and the brackets pushing out from the
 * centre. It also guards the turns — pointerenter and focus both mean "enter",
 * and without the guard arriving by both at once would count the turn twice.
 * Focus is only ever a stand-in for the pointer, so it may not overrule it:
 * opening the chat moves focus into the composer (ai_chat/Panel.jsx), and that
 * blur must not read as the pointer leaving a mark it is still resting on.
 *
 * The brackets pushing out is why the mark is a component and not an <img>.
 */
const LOGO_HOVER_DEG = 60; //< per pointer enter (and back out on leave)
const LOGO_CLICK_DEG = 120; //< per click; one full bracket, hence invisible at rest
const LOGO_MS = 100;
// How far the brackets travel from the centre ring, in viewBox units. The
// mark's viewBox is 200 wide.
const LOGO_SPREAD = 10;

interface Props {
    /** Whether the chat is open — the state a click leaves. */
    open: boolean;
    onClick: () => void;
}

export const Logo = ({ open: isOpen, onClick }: Props): ReactElement => {
    const isNavLabelShown = useAtomValueRawSync(isNavLabelShownAtom);
    const shouldShowWordmark = isOpen || isNavLabelShown;
    const [isHovered, setHovered] = useState(false);
    // Angle and pace move together: whichever interaction turns the mark also
    // says how long that turn takes, so a click's slow 120° can't inherit the
    // hover's flick or vice versa.
    const [spin, setSpin] = useState({ angle: 0, ms: LOGO_MS });
    const turn = (delta: number, ms: number): void => (
        setSpin((s) => ({ angle: s.angle + delta, ms }))
    );
    // Mirrors `isHovered` for the guard above: read synchronously inside the
    // handler, where the state value would still be the pre-update one.
    const hoveredRef = useRef(false);
    // The pointer's own presence, tracked apart from focus so a blur the user
    // didn't cause can't retract a mark they are still hovering.
    const pointerInsideRef = useRef(false);

    const enter = (): void => {
        if (hoveredRef.current) {
            return;
        }
        hoveredRef.current = true;
        setHovered(true);
        turn(LOGO_HOVER_DEG, LOGO_MS);
    };
    const leave = (): void => {
        if (!hoveredRef.current) {
            return;
        }
        hoveredRef.current = false;
        setHovered(false);
        turn(-LOGO_HOVER_DEG, LOGO_MS);
    };
    const action = isOpen ? 'Close AI chat' : 'Open AI chat';

    return (
        <Hint
            label={action}
            side="right"
        >
            <button
                type="button"
                // `open` is the state the click is leaving: closing winds forward,
                // opening unwinds, each over the drawer's own slide.
                onClick={(): void => {
                    turn(isOpen ? LOGO_CLICK_DEG : -LOGO_CLICK_DEG, CHAT_SLIDE_MS);
                    onClick();
                }}
                onPointerEnter={(): void => {
                    pointerInsideRef.current = true;
                    enter();
                }}
                onPointerLeave={(): void => {
                    pointerInsideRef.current = false;
                    leave();
                }}
                onFocus={enter}
                onBlur={(): void => {
                    if (!pointerInsideRef.current) {
                        leave();
                    }
                }}
                aria-label={action}
                aria-pressed={isOpen}
                className={cn(
                    'absolute left-4 top-4 z-20 flex items-center gap-2 rounded-full',
                    'focus:outline-none focus-visible:ring-2 focus-visible:ring-occ-brand',
                )}
            >
                <span className="relative inline-flex size-9 shrink-0">
                    {/* Hover ring: a sharp 2px circular border sitting just outside the
                mark. Absolutely positioned, so it costs no layout — the mark
                neither grows nor shifts — and it is a real border rather than a
                `ring-*` utility, which would render as a box-shadow. The ring and
                the turn are the logo's hover in place of a fill: the logo is the
                brand mark, not a button face. */}
                    <span
                        aria-hidden="true"
                        className={cn(
                            'pointer-events-none absolute -inset-0.5 rounded-full border-2',
                            'border-occ-brand-100',
                            'transition-opacity duration-100 ease-in-out',
                            isHovered ? 'opacity-100' : 'opacity-0',
                        )}
                    />
                    <OccurraMark
                        angle={spin.angle}
                        spread={isHovered ? LOGO_SPREAD : 0}
                        durationMs={spin.ms}
                        className="relative size-9"
                    />
                </span>
                {/* Fades with the drawer's slide, as the drawer's × does. `display`
                    turns off only after the fade, so a hidden wordmark keeps the
                    button no wider than the mark. */}
                <span
                    className={cn(
                        'text-sm font-semibold tracking-[0.2em] text-occ-brand',
                        'transition-[opacity,display] transition-discrete starting:opacity-0',
                        !shouldShowWordmark && 'hidden opacity-0',
                    )}
                    style={{ transitionDuration: `${CHAT_SLIDE_MS}ms` }}
                >
                    OCCURRA
                </span>
            </button>
        </Hint>
    );
};
